import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {PermissionAccess} from 'components/common/PermissionAccess';
import {DateRange} from 'components/ui-kit/DateRange';
import {EndlessList, mapListConfig} from 'components/ui-kit/EndlessList2.0';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ProductOperation} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$, isNkRole$} from 'models/auth';
import {filtersChanged, filtersReset, operationsTableState$} from 'models/products/detail';
import {breakpoint} from 'theme';
import {GridFieldConfig, UserPermission} from 'types';
import {
    ProductOperationsAccessors,
    ProductOperationsDictionary,
    ProductOperationsFormattedRow,
    ProductOperationsRawRow,
} from 'types/products-detail';
import {useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {mapEnumToI18nOptions, translateOperationReason} from 'utils/select';

import {FlexRowElement, ListRow, Text} from './styled';

const getGridColumns = (
    t: TFunction,
    dictionary: ProductOperationsDictionary,
): GridFieldConfig[] => [
    {
        id: ProductOperationsAccessors.documentType,
        header: t('pages.products.detail.operations.operation'),
        width: 160,
        filter: (
            <Select
                isClearable
                options={mapEnumToI18nOptions(
                    ProductOperation,
                    'pages.products.detail.operations.operationType',
                )}
            />
        ),
        isSortable: false,
    },
    {
        id: ProductOperationsAccessors.date,
        header: t('pages.products.detail.operations.date'),
        width: 170,
        filter: <DateRange />,
        isSortable: false,
    },
    // {
    //     id: ProductOperationsAccessors.orderId,
    //     header: t('pages.products.detail.operations.orderId'),
    //     width: 250,
    //     disableSortBy: true,
    // },
    {
        id: ProductOperationsAccessors.documentId,
        header: t('pages.products.detail.operations.documentId'),
        width: 250,
        filter: <Input placeholder={t('pages.products.grid.enterValue')} />,
        isSortable: false,
    },
    {
        id: ProductOperationsAccessors.clientName,
        header: t('pages.products.detail.operations.clientName'),
        width: 250,
        filter: <Input placeholder={t('pages.products.grid.enterValue')} />,
        isSortable: false,
    },
    {
        id: ProductOperationsAccessors.fromStorageLocationName,
        header: t('pages.products.detail.operations.fromStorageLocationName'),
        width: 220,
        filter: <Select isClearable options={dictionary.fromStorageLocation} />,
        isSortable: false,
    },
    {
        id: ProductOperationsAccessors.toStorageLocationName,
        header: t('pages.products.detail.operations.toStorageLocationName'),
        width: 220,
        filter: <Select isClearable options={dictionary.toStorageLocation} />,
        isSortable: false,
    },
    {
        id: ProductOperationsAccessors.reason,
        header: t('pages.products.detail.operations.reason'),
        width: 220,
        filter: (
            <Select
                isClearable
                options={dictionary.reason.map(({value, operationAction}) => ({
                    value,
                    label: translateOperationReason(operationAction, value, t),
                }))}
            />
        ),
        isSortable: false,
    },
];

const convertDocumentNumber = (
    elem: ProductOperationsRawRow,
    isCustomsRole?: boolean,
    isNkRole?: boolean,
) => {
    if (isCustomsRole) {
        return handleNil(elem.reportId, (id) => id);
    }
    if (elem.source === 'CUSTOMS')
        return handleNil(elem.reportId, (id) => (
            <Link to={generatePath(ROUTE.DOCUMENTS_GDS_DETAIL, {id})}>{id}</Link>
        ));

    if (elem.operationAction === ProductOperation.GENERATED) {
        return handleNil(elem.orderId, (value) => (
            <PermissionAccess
                permissions={[
                    UserPermission.ORDER_CREATE_CUSTOMER,
                    UserPermission.ORDER_CREATE_TRANSPORT,
                    UserPermission.ORDER_CREATE_CUSTOMS,
                ]}
                placeholder={value}
            >
                <Link to={generatePath(ROUTE.ORDERS_DETAIL, {id: value})}>{value}</Link>
            </PermissionAccess>
        ));
    }

    if (elem.operationAction === ProductOperation.SOLD) {
        return handleNil(elem.reportId, (value) => (
            <PermissionAccess
                permissions={[
                    UserPermission.ORDER_CREATE_CUSTOMER,
                    UserPermission.ORDER_CREATE_TRANSPORT,
                    UserPermission.ORDER_CREATE_CUSTOMS,
                ]}
                placeholder={value}
            >
                <Link to={generatePath(ROUTE.RECEIPTS_DETAIL, {id: value})}>{value}</Link>
            </PermissionAccess>
        ));
    }

    if (elem.operationAction === ProductOperation.DEPARENTED) {
        return handleNil(elem.reportId);
    }

    return handleNil(elem.reportId, (value) => {
        if (isNkRole) return value;

        if (ProductOperation.SALE_BY_INVOICE === elem.operationAction)
            return <Link to={generatePath(ROUTE.INVOICES_DETAIL, {id: value})}>{value}</Link>;

        if (
            [
                ProductOperation.SALE_BY_TRANSFER_DOCUMENT,
                ProductOperation.TRANSFERRED_BY_TRANSFER_DOCUMENT,
                ProductOperation.RECEIVED_BY_TRANSFER_DOCUMENT,
            ].includes(elem.operationAction)
        )
            return (
                <Link to={generatePath(ROUTE.DOCUMENTS_TRANSFER_DETAIL, {id: value})}>{value}</Link>
            );

        if (elem.operationAction !== ProductOperation.ISSUED) {
            return <Link to={generatePath(ROUTE.DOCUMENTS_DETAIL, {id: value})}>{value}</Link>;
        }

        return '—';
    });
};

export function convertData(
    data: ProductOperationsRawRow[] | undefined,
    t: TFunction,
    isCustomsRole?: boolean,
    isNkRole?: boolean,
): ProductOperationsFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [ProductOperationsAccessors.documentType]: handleNil(
            elem.operationAction,
            (value: ProductOperation) =>
                t('pages.products.detail.operations.operationType.' + value),
        ),
        [ProductOperationsAccessors.date]: handleNil(elem.operationTime, (value: string) =>
            formatDate(value, 'dd.MM.yyyy HH:mm'),
        ),
        [ProductOperationsAccessors.documentId]: convertDocumentNumber(
            elem,
            isCustomsRole,
            isNkRole,
        ),
        [ProductOperationsAccessors.clientName]: handleNil(elem.clientName),
        [ProductOperationsAccessors.fromStorageLocationName]: handleNil(
            elem.fromStorageLocationName,
        ),
        [ProductOperationsAccessors.toStorageLocationName]: handleNil(elem.toStorageLocationName),
        [ProductOperationsAccessors.reason]: handleNil(elem.reason, (value) =>
            translateOperationReason(elem.operationAction, value, t),
        ),
    }));
}

export const Operations = () => {
    const {t} = useTranslation();
    const {data, filters, isLoading, dictionary} = useStore(operationsTableState$);
    const gridColumns = React.useMemo(() => getGridColumns(t, dictionary), [t, dictionary]);
    const columns = React.useMemo(
        () => mapTableColumns(gridColumns, filters, filtersChanged, filtersReset),
        [gridColumns, filters],
    );
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const isCustomsRole = useStore(isCustomsRole$);
    const isNkRole = useStore(isNkRole$);
    const mappedData = React.useMemo(() => convertData(data, t, isCustomsRole, isNkRole), [
        data,
        t,
        isCustomsRole,
        isNkRole,
    ]);

    return (
        <div>
            {!isMobile && (
                <Table
                    columns={columns}
                    data={mappedData}
                    totalRowsCount={data.length}
                    isLoading={isLoading}
                    hideSetting
                />
            )}

            {isMobile && (
                <EndlessList<ProductOperationsFormattedRow>
                    rows={mappedData}
                    configurationBlock={mapListConfig(gridColumns)}
                    totalRowsCount={data.length}
                    renderRow={(row: ProductOperationsFormattedRow) => (
                        <ListRow>
                            <FlexRowElement>
                                <Text>{row[ProductOperationsAccessors.date]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                {row[ProductOperationsAccessors.documentId]}
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[ProductOperationsAccessors.documentType]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[ProductOperationsAccessors.clientName]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>
                                    {row[ProductOperationsAccessors.fromStorageLocationName]}
                                </Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[ProductOperationsAccessors.toStorageLocationName]}</Text>
                            </FlexRowElement>
                            <FlexRowElement>
                                <Text>{row[ProductOperationsAccessors.reason]}</Text>
                            </FlexRowElement>
                        </ListRow>
                    )}
                />
            )}
        </div>
    );
};
